import { CheckIcon } from '@primer/octicons-react';
import React, { Suspense } from 'react';
import { graphql, useFragment } from 'react-relay';

import useLazyLoadQuery from '../../../hooks/useLazyLoadQuery';
import { TaskMigrateDialog_coursesQuery } from '../../../relay/__generated__/TaskMigrateDialog_coursesQuery.graphql';
import { TaskMigrateDialog_task$key } from '../../../relay/__generated__/TaskMigrateDialog_task.graphql';
import { TaskMigrateDialog_taskMigrateMutation } from '../../../relay/__generated__/TaskMigrateDialog_taskMigrateMutation.graphql';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import View from '../../core/View';
import UnitDSelectDialogTokenField from '../../unitD/UnitDSelectDialogTokenField';

const TaskMigrateDialog_task = graphql`
  fragment TaskMigrateDialog_task on Task {
    id
    course {
      id
      year
    }
    publishedUnitDs(order: { order: DESC }) {
      id
      description
      nextUnitD {
        id
        description
      }
      previousUnitD {
        id
        description
      }
    }
  }
`;

const coursesForTaskMigrateDialog = graphql`
  query TaskMigrateDialog_coursesQuery {
    courses {
      edges {
        node {
          id
        }
      }
    }
  }
`;

type Props = { task: TaskMigrateDialog_task$key } & DialogProps &
  Pick<MutationFormikProps<TaskMigrateDialog_taskMigrateMutation, { manualUnitDs: string[] }>, 'config'>;

const TaskMigrateDialog = ({ task: taskReference, config, ...props }: Props) => {
  const task = useFragment(TaskMigrateDialog_task, taskReference);
  const { id, course } = task;
  // 문항 교육과정에 따라 대응 단원을 고른다: 2015 → 2022 는 nextUnitD, 2022 → 2015 는 previousUnitD
  const publishedUnitDs = [...task.publishedUnitDs].reverse().map(({ nextUnitD, previousUnitD, ...unitD }) => ({
    ...unitD,
    mappedUnitD: course?.year === 2022 ? previousUnitD : nextUnitD,
  }));

  const autoUnitDs = Array.from(
    new Set(publishedUnitDs.flatMap(({ mappedUnitD }) => (mappedUnitD ? [mappedUnitD.id] : []))),
  );
  const needManual = publishedUnitDs.some(({ mappedUnitD }) => !mappedUnitD);

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>마이그레이션하기</Text>
      </Dialog.Header>
      <MutationFormik<TaskMigrateDialog_taskMigrateMutation, { manualUnitDs: string[] }>
        mutation={graphql`
          mutation TaskMigrateDialog_taskMigrateMutation($input: TaskMigrateInput!) {
            taskMigrate(input: $input) {
              id
            }
          }
        `}
        initialValues={{ id, unitDs: autoUnitDs, manualUnitDs: [] }}
        valuesTransformer={({ manualUnitDs, ...values }) => ({
          ...values,
          unitDs: Array.from(new Set([...autoUnitDs, ...manualUnitDs])),
        })}
        config={config}
      >
        {({ handleSubmit }) => (
          <>
            <Dialog.Body>
              <NonFieldError sx={{ marginBottom: 3 }} />
              <FormLayout>
                <View>
                  <Text fontSize={2} fontWeight={'bold'}>
                    단원 매핑
                  </Text>
                  {publishedUnitDs.map(({ id: unitDId, description, mappedUnitD }) => (
                    <View key={unitDId} sx={{ marginTop: 2 }}>
                      <Text fontSize={1}>{description}</Text>
                      <Text fontSize={1} color={mappedUnitD ? 'fg.muted' : 'danger.fg'}>
                        {' → '}
                        {mappedUnitD ? mappedUnitD.description : '매핑 단원 없음 (아래에서 직접 선택)'}
                      </Text>
                    </View>
                  ))}
                </View>
                {needManual ? (
                  <Suspense
                    fallback={
                      <View padding={2}>
                        <Spinner size={'small'} />
                      </View>
                    }
                  >
                    <MigrateUnitDSelectField currentCourseId={course?.id} />
                  </Suspense>
                ) : null}
              </FormLayout>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant={'primary'} leadingIcon={CheckIcon} size={'large'} onClick={() => handleSubmit()}>
                마이그레이션하기
              </Button>
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

const MigrateUnitDSelectField = ({ currentCourseId }: { currentCourseId?: string }) => {
  const [{ courses }] = useLazyLoadQuery<TaskMigrateDialog_coursesQuery>(
    coursesForTaskMigrateDialog,
    {},
    { fetchPolicy: 'store-or-network' },
  );
  // 항상 문제와 다른 교육과정의 단원에서 선택
  const otherCourseIds = courses.edges.map(({ node }) => node.id).filter((courseId) => courseId !== currentCourseId);

  return (
    <UnitDSelectDialogTokenField
      required
      label={'마이그레이션할 단원'}
      name={'manualUnitDs'}
      placeholder={'Unit A > B > C > D 선택'}
      caption={'매핑 단원이 없는 단원만 직접 선택해요'}
      variables={{ filters: { courseId_In: otherCourseIds } }}
    />
  );
};

export default TaskMigrateDialog;
